// How a stage stands IN ITS RUN, and the engine's words put plainly, pure
// (no React) so stageState.selftest.ts runs it on its own.
//
//   A stage's state   stageStatus (../status.ts) read against the run: a
//                     stage an ended run never reached is "Not run" (never
//                     "Waiting"), with why; a fan-out whose failed branch
//                     stopped the run is Failed, one whose failed branches
//                     the run carried past is Done with failures (amber);
//                     a job's step says what it came to beside its run
//                     state (a refused plan, a check's finding: the job
//                     page's words, ../jobs/jobModel.ts), a step whose work
//                     a check sent back says so, and a step that failed and
//                     the Lead then dealt with at a checkpoint says that.
//   Reasons           a node's error as a sentence: "over_max_items: branch
//                     13 is over the fan-out cap of 12" → "Not run: over the
//                     limit of 12 branches". Red only for a real failure.
//   The run's end     why a run failed, in words (a refused plan: "The job
//                     did not start: the Lead's plan needed 13 minutes, but
//                     only 11 were left before the reserve."), the outcome
//                     the inspector heads with, and the tone of the events
//                     that end a run or a job (by how it ended).
//   A job's run       its clock from the run's own job (the deadline, the
//                     time left: the job page's words), people by name
//                     ("asked by Julia"), the Lead's lines with names for
//                     its ids ("(paper-reader, quant)" → "(Paper Reader,
//                     Quant)").

import type { NodeState, RunEvent, RunState, StageSpec } from "@/api/workflowRuns";
import type { StatusTone } from "@/components/ui/StatusBadge";
import { plainConclusion } from "@/lib/verdict";

import { checkStatusWord, plainRefusal, stepStatusWord } from "../jobs/jobModel";
import {
  STATUS,
  doneFailedWord,
  eventTone,
  hasEnded,
  isFailedNode,
  isLiveRun,
  isNotRun,
  isWaitingNode,
  makeStatusWord,
  stageStatus,
  statusWord,
  unitWords,
  type StageStatus,
  type StatusWord,
} from "../status";
import { localTimes } from "../time";

type Run = Pick<RunState, "status" | "error" | "conclusion" | "nodes" | "workflow"> & { job?: RunState["job"] };

// ── reasons ──

export { runJobClock } from "./jobClock";

const who = (actor: string) => (actor.trim() === "operator" ? "you" : "");

// The engine's codes and fixed phrases, most specific first (services/workflows/engine.py, graph.py; jobs/*).
const REASONS: [RegExp, (m: RegExpExecArray) => string][] = [
  [/^over_max_items: branch \d+ is over the fan-out cap of (\d+)/, (m) => `Not run: over the limit of ${m[1]} copies`],
  [/^over_max_items\b/, () => "Not run: over the step's limit of copies"],
  [/^upstream_failed\b/, () => "Not run: a step it depends on failed, and the workflow stops on that"],
  [/^upstream_skipped\b/, () => "Not run: every step it depends on ended without a result"],
  [/^no_upstream_items\b/, () => "Not run: the step before it handed on no items"],
  [/^(?:run_stopped\b|the run stopped before this stage started)/, () => "Not run: the run stopped before it started"],
  [/^run cancelled by (.+)$/, (m) => (who(m[1]) ? "Not run: you stopped the run first" : "Not run: the run was stopped first")],
  [/^cancelled by (.+)$/, (m) => (who(m[1]) ? "Stopped when you stopped the run" : "Stopped with the run")],
  [/^run stopped: \S+ (failed|timed out)/, (m) => `Stopped: another step ${m[1]}, and the run stops on that`],
  [/^upstream \S+ retried by /, () => "Stopped: an earlier step is being run again"],
  [/^no_items: finish recorded no items\b/, () => "Finished without a list for the next step"],
  [/^no_items: ([\s\S]+)$/, (m) => `Finished without a usable list for the next step: ${m[1]}`],
  [/^sandbox_not_ready: ([\s\S]+)$/, (m) => `Its workspace was not ready: ${m[1]}`],
  [/^Request timed out before a response was generated\b/, () => "Ran out of time before it answered"],
  // the engine's watchdog (corvus_lea engine.py): a step that ran far past its limit, Lattice out of touch with the agents or not
  [/^still running long past its time limit( while the gateway was unreachable)?; stopped by lattice/, (m) => `It ran far past its time limit${m[1] ? " while Corvus could not reach the agents" : ""}, so Corvus stopped it`],
  // a subagent's script that ran out of memory ("the run ended with an error: Python raised MemoryError while …")
  [/^(?:the run ended with an error:\s*)?(?:Python raised )?MemoryError\b:?\s*([\s\S]*)$/, (m) => `It ran out of memory${m[1].trim() ? ` ${m[1].trim()}` : ""}`],
  // a stage given a thinking level its model does not have ("… for corvus_inference/kimi-k3. Use one of: off.")
  [
    /^Thinking level "([^"]+)" is not supported for \S+?\.\s*Use one of: ([^.]+)\./,
    (m) => `This model cannot think at the ${upperFirst(m[1])} level; set the thinking to ${upperFirst(firstOption(m[2]))} and run it again`,
  ],
];

const upperFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const firstOption = (list: string) => list.split(/[,\s]+/).filter(Boolean)[0] ?? "off";
/** The engine's "(after 3 continue turns)" tail: how often it asked the subagent to carry on, not why it failed. */
const AFTER_TURNS = /\s*\(after \d+ continue turns?\)\s*$/;

/** A node's error (an engine code, a fixed phrase) as a sentence; anything else as it is, its UTC times in the operator's zone. */
export function plainReason(error: string | null | undefined): string {
  const text = (error ?? "").trim().replace(AFTER_TURNS, "");
  if (!text) return "";
  for (const [re, say] of REASONS) {
    const m = re.exec(text);
    if (m) return say(m);
  }
  const local = localTimes(text);
  return local.charAt(0).toUpperCase() + local.slice(1);
}

/** The name of a branch: its item's label, else the role it acts as, else its key, else its number. */
export function branchLabel(node: NodeState): string {
  const item = node.item as { label?: unknown; key?: unknown; role?: unknown } | null | undefined;
  if (item && typeof item.label === "string" && item.label.trim()) return item.label.trim();
  if (node.role?.trim()) return node.role.trim();
  if (item && typeof item.role === "string" && item.role.trim()) return item.role.trim();
  if (item && typeof item.key === "string" && item.key.trim()) return item.key.trim();
  if (node.branch != null) return `Copy ${node.branch + 1}`;
  return node.name || node.id;
}

/** A piece of work by name: the stage's name, and its branch in brackets ("Collect venue data (bybit)"). The last block is Report, whatever the frozen copy calls it. */
export function pieceName(run: Pick<Run, "workflow">, node: NodeState): string {
  if (node.handover || node.stage === "handover") return "Report";
  const name = run.workflow.stages.find((s) => s.id === node.stage)?.name || node.stage || node.id;
  return node.branch != null ? `${name} (${branchLabel(node)})` : name;
}

/** The pieces of work that failed or ran out of time, in the run's order of stages. */
export function unfinishedNodes(run: Pick<Run, "workflow" | "nodes">): NodeState[] {
  const order = new Map(run.workflow.stages.map((s, i) => [s.id, i]));
  return Object.values(run.nodes ?? {})
    .filter((n) => isFailedNode(n.status))
    .sort((a, b) => (order.get(a.stage) ?? 99) - (order.get(b.stage) ?? 99) || (a.branch ?? 0) - (b.branch ?? 0));
}

/** Red for a real failure (failed, timed out); grey for work that was skipped or stopped on purpose. */
export function reasonTone(status: string | null | undefined): "danger" | "muted" {
  return isFailedNode(status) ? "danger" : "muted";
}

// ── the stage in its run ──

export interface StageState {
  /** The stage's status in its run: stageStatus, "skipped" for one an ended run never reached, "failed" for a fan-out whose failure stopped the run. */
  status: StageStatus;
  /** What to say: the status's word, "Not run", or a job step's outcome ("Refused", "Passed after 1 send-back"). Its tone and look draw it. */
  word: StatusWord;
  /**
   * One line on why it stands so ("Not run: the run was stopped first",
   * "The Lead dropped it at Checkpoint 1"), or the Lead's own line with
   * names for its ids; "" for none. It replaces an ended stage's own line.
   */
  note: string;
  noteTone: "danger" | "muted";
  /** Short marks beside the word: "Sent back once". */
  chips: string[];
}

export const NOT_RUN: StatusWord = makeStatusWord("Not run", "neutral", "skipped", "notRun");

/** "Sent back once", "Sent back 2 times"; "" for none. */
export function sentBackLabel(n: number): string {
  if (!(n > 0)) return "";
  return n === 1 ? "Sent back once" : `Sent back ${n} times`;
}

/**
 * A job whose plans were all refused: no plan, attempts made, and its plan
 * step has ended. Never a cancelled job: one cancelled while the Lead
 * re-planned was cancelled, not refused.
 */
export function planRefused(run: Run | null | undefined): boolean {
  const job = run?.job;
  if (!job || job.plan || !(job.plan_attempts > 0) || run.status === "cancelled") return false;
  const plan = run.workflow.stages.find((s) => s.job?.kind === "plan");
  const nodes = plan ? Object.values(run.nodes ?? {}).filter((n) => n.stage === plan.id) : [];
  return (run.error ?? "").startsWith("plan_refused:") || (nodes.length > 0 && nodes.every((n) => hasEnded(n.status)));
}

/** Why a stage an ended run never reached did not run. */
function notRunWhy(run: Run): string {
  const u = unitWords(!!run.job).stage[0];
  if (run.status === "cancelled") return "Not run: the run was stopped first";
  if (planRefused(run)) return "Not run: the Team task stopped at planning";
  if (run.status === "failed") {
    return Object.values(run.nodes ?? {}).some((n) => isFailedNode(n.status)) ? `Not run: an earlier ${u} failed and the run stopped` : "Not run: the run stopped before it started";
  }
  return "Not run: the run ended before it started";
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** An id as a whole token of a text ("build" in "build failed", not in "build-core"). */
const TOKEN = (id: string) => new RegExp(`(?<![\\w-])${escape(id)}(?![\\w-])`);

/**
 * What the Lead did with a step that failed, timed out or was stopped: the
 * first checkpoint decision after it ended that names it (drops it, has it
 * redone, or plans around it); "" when none did.
 */
export function leadAnswer(run: Run, stage: StageSpec, nodes: readonly NodeState[]): string {
  const job = run.job;
  if (!job?.revisions?.length) return "";
  const ended = nodes.map((n) => Date.parse(n.ended_at)).filter(Number.isFinite);
  const since = ended.length ? Math.min(...ended) : 0;
  const named = (id: string) => id === stage.id || nodes.some((n) => n.id === id);
  for (const r of job.revisions) {
    if (r.kind !== "decision" && r.kind !== "stop") continue;
    if (since && Date.parse(r.at) < since) continue;
    const at = run.workflow.stages.find((s) => s.id === r.by)?.name || "a checkpoint";
    if ((r.dropped ?? []).some(named)) return `The Lead dropped it at ${at}`;
    if ((r.redone ?? []).some(named)) return `The Lead had it redone at ${at}`;
    if (TOKEN(stage.id).test(r.summary ?? "")) return `The Lead replanned around it at ${at}`;
  }
  return "";
}

/** What the run view knows beside the run: a job step's outcome by stage id (RunDetail.step_outcomes), subagent names by template. */
export interface StageContext {
  outcomes?: Readonly<Record<string, string>> | null;
  names?: Readonly<Record<string, string>> | null;
}

/**
 * A line the Lead wrote (its plan's or a checkpoint's conclusion) with the
 * ids in it said as names: a team key or a subagent as the subagent's name
 * or the role, a step as its title ("2 in the team (paper-reader, quant)"
 * → "(Paper Reader, Quant)"), its UTC times in the operator's zone.
 */
export function leadLine(text: string, run: Run | null | undefined, names?: Readonly<Record<string, string>> | null): string {
  if (!text.trim()) return "";
  const said = new Map<string, string>();
  for (const s of run?.workflow.stages ?? []) if (s.name && s.name !== s.id) said.set(s.id, s.name);
  for (const [t, n] of Object.entries(names ?? {})) if (n && n !== t) said.set(t, n);
  for (const m of run?.job?.plan?.team ?? []) {
    const who = m.subagent ? names?.[m.subagent] || m.subagent : m.role || "";
    if (who && who !== m.key) said.set(m.key, who);
  }
  if (!said.size) return localTimes(text);
  // one pass (a name put in is never read again), longest first so "build-run-all" wins over "build"
  const ids = [...said.keys()].sort((a, b) => b.length - a.length).map(escape);
  const any = new RegExp(`(?<![\\w-])(?:${ids.join("|")})(?![\\w-])`, "g");
  return localTimes(text.replace(any, (id) => said.get(id) ?? id));
}

/**
 * How a stage stands in its run. `ctx.outcomes` is RunDetail.step_outcomes
 * (a job step's outcome by stage id); without it a refused plan is still
 * known from the run's job. `ctx.names` says the Lead's lines by name.
 */
export function stageStateIn(stage: StageSpec, nodes: readonly NodeState[], run: Run | null | undefined, ctx: StageContext = {}): StageState {
  const outcomes = ctx.outcomes;
  let status = stageStatus(nodes);
  let word = statusWord(status);
  let note = "";
  let noteTone: StageState["noteTone"] = "muted";
  const ended = !!run && !isLiveRun(run.status);
  const kind = stage.job?.kind;
  const outcome = outcomes?.[stage.id] || (kind === "plan" && planRefused(run) ? "refused" : "");

  if (ended && isWaitingNode(status)) {
    // an ended run will never start it: never "Waiting"
    status = "skipped";
    word = NOT_RUN;
    note = notRunWhy(run);
  } else if (status === "skipped") {
    // one word on the card: its why already opens "Not run:"
    word = NOT_RUN;
  } else if (status === "completed_with_failures" && stage.on_failure === "stop") {
    // its failed branch stopped the run: that is a failed stage, not one the run carried past
    status = "failed";
    word = STATUS.failed;
  } else if (status === "completed_with_failures") {
    const failed = nodes.filter((n) => isFailedNode(n.status)).length;
    if (failed > 0) word = doneFailedWord(failed);
  }

  if (outcome) {
    if (kind === "check") {
      const sendBacks = (run?.job?.revisions ?? []).filter((r) => r.kind === "faults" && r.by === stage.id).length;
      word = checkStatusWord({ status, outcome, send_backs: sendBacks });
    } else {
      word = stepStatusWord(status, outcome);
    }
    if (word.tone === "danger") status = "failed";
    if (outcome === "refused") {
      note = planRefusalLine(run);
      noteTone = "danger";
    }
  }

  if (!note && run?.job && (isFailedNode(status) || isNotRun(status))) note = leadAnswer(run, stage, nodes);
  // the Lead's own line (its plan, a checkpoint's decision) with names for its ids
  const conclusion = nodes.length === 1 ? nodes[0].result?.conclusion ?? "" : "";
  if (!note && (kind === "plan" || kind === "checkpoint") && conclusion.trim()) {
    const named = leadLine(conclusion, run, ctx.names);
    if (named !== localTimes(conclusion)) note = named;
  }
  const sent = nodes.reduce((sum, n) => sum + (n.send_backs ?? 0), 0);
  return { status, word, note, noteTone, chips: kind === "check" ? [] : [sentBackLabel(sent)].filter(Boolean) };
}

// ── the run's end ──

const TIME_REFUSAL = /end (\d+) minutes? after planning, but (\d+) (?:are|is) available before the reserve/;

/** Why a refused plan could not run, in one sentence; the first reason the plan check gave. */
export function planRefusalLine(run: Run | null | undefined): string {
  const reasons = run?.job?.plan_refusals?.length ? run.job.plan_refusals : (run?.error ?? "").replace(/^plan_refused:\s*/, "").split(/;\s+(?=[a-z_.]+:)/);
  const first = (reasons[0] ?? "").trim();
  const time = TIME_REFUSAL.exec(first);
  if (time) return `The Team task did not start: the Lead's plan needed ${time[1]} minutes, but only ${time[2]} were left before the reserve.`;
  const why = first ? plainRefusal(first) : "";
  return `The Team task did not start: Corvus refused the Lead's plan.${why ? ` ${why.replace(/\.?$/, ".")}` : ""}`;
}

/** A run's error as a sentence: a refused plan, a stage that stopped the run by name, the rest as the engine wrote it. */
export function runErrorText(run: Run | null | undefined): string {
  const error = (run?.error ?? "").trim();
  if (!error) return "";
  if (error.startsWith("plan_refused:") || planRefused(run)) return planRefusalLine(run);
  const stop = /^stage '([^']+)' \(([^)]+)\) (failed|timed out): ([\s\S]*)$/.exec(error);
  if (stop) {
    const name = run?.workflow.stages.find((s) => s.id === stop[1])?.name || stop[1];
    const why = plainReason(stop[4]);
    return `${name} ${stop[3]}, and the run stops on that${why ? `: ${why}` : "."}`;
  }
  return plainReason(error);
}

const NODE_STOP = /^([A-Za-z0-9_-]+(?:\[\d+\])?) (failed|timed out): ([\s\S]*)$/;

/**
 * A failed run whose error (or, with none, its conclusion: the engine's
 * `_conclusion`) names the piece of work that stopped it, "collect[2]
 * failed: …": that node, how it ended and why; null otherwise.
 */
function nodeStopOf(run: Run | null | undefined): { node: NodeState; verb: string; why: string; raw: string } | null {
  if (!run || run.status !== "failed") return null;
  const raw = ((run.error ?? "").trim() || (run.conclusion ?? "").trim());
  const m = NODE_STOP.exec(raw);
  const node = m ? run.nodes?.[m[1]] : undefined;
  return m && node ? { node, verb: m[2], why: m[3], raw } : null;
}

/**
 * What the inspector heads the run with: its outcome (never a job's plan
 * line: a job that never started says why instead) and, apart, why it
 * failed; the two never say the same thing twice.
 */
export function runOutcome(run: Run): { text: string; error: string } {
  if (planRefused(run)) return { text: planRefusalLine(run), error: "" };
  const error = runErrorText(run);
  const conclusion = (run.conclusion ?? "").trim();
  // a failed run's "collect[2] failed: …" is its failure (runFailure), not its conclusion
  const text = conclusion && conclusion !== (run.error ?? "").trim() && !nodeStopOf(run) ? localTimes(plainConclusion(conclusion)) : "";
  return { text, error };
}

/** The engine's own line for a stopped run ("Cancelled by operator."): never shown as a conclusion. */
export const CANCELLED_BY = /^Cancelled by (.+?)\.?$/;

/**
 * A run someone stopped, in words, from the run's counters: "You stopped
 * this run after 2 of 6 stages." ("This run was stopped …" when it was not
 * the operator); "" for a run that was not cancelled.
 */
export function stoppedLine(run: Run, counters: { stages_done: number; stages_total: number }): string {
  if (run.status !== "cancelled") return "";
  const by = CANCELLED_BY.exec((run.conclusion ?? "").trim())?.[1]?.trim() ?? "";
  const who = by === "operator" ? "You stopped this run" : "This run was stopped";
  const [one, many] = unitWords(!!run.job).stage;
  const total = counters.stages_total;
  return total ? `${who} after ${counters.stages_done} of ${total} ${total === 1 ? one : many}.` : `${who}.`;
}

/** A failure said once: one sentence, what to do about it ("" when the sentence says it), and the engine's own text for a Details fold ("" when the sentence is that text). */
export interface Failure {
  text: string;
  todo: string;
  raw: string;
  /** The step (node or stage id) it stopped at, for "See why"; none when the run failed as a whole. */
  at?: string;
}

const lowerFirst = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
/** The sentence already says what to do ("… and run it again"). */
const SAYS_WHAT_TO_DO = /run it again|retry it|start (?:it|the job) again/i;

/** What to do about a stage that stopped: said when the reason does not already. */
function stageTodo(verb: string, why: string, stageWord: string): string {
  if (SAYS_WHAT_TO_DO.test(why)) return "";
  if (/ran out of memory/i.test(why)) return "Ask it for less data.";
  if (verb === "timed out" || /ran out of time/i.test(why)) return `Give the ${stageWord} more time.`;
  return `Open the ${stageWord} under Steps to see what it did, then retry it.`;
}

/**
 * Why a run failed, once, in plain words, with what to do: a refused plan
 * (start again with more time or a smaller brief), a stage that stopped the
 * run ("Stopped at Plan: this model cannot think at the High level; set the
 * thinking to Off and run it again."), else the error as a sentence. The
 * engine's text rides along for a Details fold. Null when the run has no
 * error.
 */
export function runFailure(run: Run | null | undefined): Failure | null {
  const at = nodeStopOf(run);
  if (run && at) {
    const why = plainReason(at.why);
    const where = `Stopped at ${pieceName(run, at.node)}`;
    const text = why ? `${where}: ${lowerFirst(why).replace(/\.?$/, ".")}` : `${where}: it ${at.verb}.`;
    // the Details fold: the engine's words, the step by its name rather than its id
    return { text, todo: stageTodo(at.verb, why, unitWords(!!run.job).stage[0]), raw: at.raw.replace(at.node.id, pieceName(run, at.node)), at: at.node.id };
  }
  const raw = (run?.error ?? "").trim();
  if (!run || !raw) return null;
  const stageWord = unitWords(!!run.job).stage[0];
  if (raw.startsWith("plan_refused:") || planRefused(run)) {
    return { text: planRefusalLine(run), todo: "Start the Team task again with more time or a smaller brief.", raw };
  }
  const stop = /^stage '([^']+)' \(([^)]+)\) (failed|timed out): ([\s\S]*)$/.exec(raw);
  if (stop) {
    const name = stop[1] === "handover" ? "Report" : run.workflow.stages.find((s) => s.id === stop[1])?.name || stop[1];
    const why = plainReason(stop[4]);
    const first = run.workflow.stages[0]?.id === stop[1];
    const where = first ? `Stopped at the first ${stageWord}, ${name}` : `Stopped at ${name}`;
    const text = why ? `${where}: ${lowerFirst(why).replace(/\.?$/, ".")}` : `${where}: it ${stop[3]}.`;
    return { text, todo: stageTodo(stop[3], why, stageWord), raw, at: stop[1] };
  }
  const text = plainReason(raw).replace(/\.?$/, ".");
  return { text, todo: "", raw: text.replace(/\.$/, "") === raw.replace(/\.$/, "") ? "" : raw };
}

/**
 * Why one stage run (or a branch) did not finish, in a sentence, with what
 * to do: a timeout against its own limit ("It ran out of time: 18 min
 * against a 12 min limit."), else its error as a sentence (plainReason).
 * `elapsed` is how long it ran in words ("18 min"), when known.
 */
export function nodeFailure(node: Pick<NodeState, "status" | "error" | "limit_minutes">, elapsed = "", stageWord = "step"): Failure | null {
  const raw = (node.error ?? "").trim();
  if (node.status === "timed_out") {
    const limit = node.limit_minutes ? `${node.limit_minutes} min limit` : "";
    const text = elapsed && limit ? `It ran out of time: ${elapsed} against a ${limit}.` : limit ? `It ran out of time: it hit its ${limit}.` : "It ran out of time.";
    return { text, todo: `Give the ${stageWord} more time and retry it.`, raw };
  }
  if (!raw) return null;
  const said = plainReason(raw);
  const text = said.replace(/\.?$/, ".");
  const same = said === raw || said === raw.charAt(0).toUpperCase() + raw.slice(1);
  const todo = /ran out of memory/i.test(said) ? "Ask it for less data." : ""; // Retry is the panel's own button
  return { text, todo, raw: same ? "" : raw };
}

/** Who a person is inside a sentence: "you" for the operator, else their name ("asked by Julia"). */
export function personInline(template: string | null | undefined, nameOf: (t: string) => string): string {
  const t = (template ?? "").trim();
  if (!t || t === "operator") return "you";
  const name = nameOf(t);
  return name === "You" ? "you" : name || t;
}

// ── events ──

const ENDED_AS = /^(?:run |job "[\s\S]*" )(completed with failures|completed_with_failures|completed|failed|cancelled)\b/;

/** How a run's or a job's end event says the run ended (its `status`, else its message); null for another event. */
export function endedStatus(e: Pick<RunEvent, "kind" | "message" | "extra">): string | null {
  if (e.kind !== "run_finished" && e.kind !== "job_finished") return null;
  const s = e.extra?.status;
  if (typeof s === "string" && STATUS[s]) return s;
  const m = ENDED_AS.exec(e.message ?? "");
  return m ? m[1].replace(/ /g, "_") : null;
}

/**
 * An event's tone: the end of a run or a job by how it ended (red failed,
 * amber done with failures, grey cancelled; never green for a failure), a
 * plan refusal that ended the job red (amber while the Lead may try
 * again); the rest as ../status.ts eventTone says.
 */
export function eventToneOf(e: Pick<RunEvent, "kind" | "message" | "extra">): StatusTone {
  const ended = endedStatus(e);
  if (ended) return statusWord(ended).tone;
  if (e.kind === "plan_refused") return e.extra?.final === true || /job stops/.test(e.message ?? "") ? "danger" : "warning";
  return eventTone(e.kind);
}
